/**
 * Snapshot fetch + state transitions for AccesszenProvider, kept free of React so the
 * stale-response race is testable in vitest's node env (no jsdom in platform).
 * Ref: ASC-10848
 */

import type { PermissionSnapshot } from '@platform/accesszen-contracts'
import { PermissionSnapshotSchema } from '@platform/accesszen-contracts'
import type { CacheStorage } from './snapshot-cache'
import { clearSnapshotCache, readSnapshotCache, writeSnapshotCache } from './snapshot-cache'

export type SnapshotSink = {
  setSnapshot: (snapshot: PermissionSnapshot | null) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
}

export type LoadSnapshotOptions = {
  endpoint: string
  orgId: string
  /** Keep the current snapshot and loading=false while fetching (warm cache / revalidate). */
  silent: boolean
  /**
   * False once a newer fetch has started or the org changed. A superseded response —
   * success, error, or 401 — must not touch state or cache: an org-A fetch still in
   * flight after a switch to org B would otherwise paint A's grants over B.
   */
  isCurrent: () => boolean
  sink: SnapshotSink
  fetchImpl?: typeof fetch
  storage?: CacheStorage | null
}

export type OrgSnapshotState = { snapshot: PermissionSnapshot | null; loading: boolean }

/** Initial state for an org: its cached snapshot (no loading flash) or empty + loading. */
export function stateForOrg(orgId: string, storage?: CacheStorage | null): OrgSnapshotState {
  const snapshot = readSnapshotCache(orgId, storage)
  return { snapshot, loading: snapshot === null }
}

export async function loadSnapshot({
  endpoint,
  orgId,
  silent,
  isCurrent,
  sink,
  fetchImpl = fetch,
  storage,
}: LoadSnapshotOptions): Promise<void> {
  if (!silent) sink.setLoading(true)
  sink.setError(null)

  try {
    const response = await fetchImpl(`${endpoint}/snapshot`, {
      credentials: 'include', // JWT in HTTP-only cookie — auto-sent by browser
      headers: { 'Content-Type': 'application/json' },
    })
    if (!isCurrent()) return

    if (response.status === 401 || response.status === 403) {
      // Not authenticated — clear snapshot and cache silently, no error state
      sink.setSnapshot(null)
      clearSnapshotCache(storage)
      return
    }

    if (!response.ok) {
      throw new Error(`Failed to fetch permissions: ${response.status}`)
    }

    const json: unknown = await response.json()
    if (!isCurrent()) return
    const result = PermissionSnapshotSchema.safeParse(json)
    if (!result.success) {
      throw new Error(`Permission snapshot validation failed: ${result.error.message}`)
    }
    // Second line of defence: a snapshot the server built for another org never
    // reaches this org's UI, mirroring the guard in writeSnapshotCache.
    if (result.data.org_id !== orgId) return
    sink.setSnapshot(result.data)
    writeSnapshotCache(result.data, orgId, storage)
  } catch (err) {
    if (!isCurrent()) return
    const message = err instanceof Error ? err.message : 'Unknown error'
    sink.setError(message)
    // On silent background refresh, keep the stale snapshot rather than clearing it.
    if (!silent) sink.setSnapshot(null)
  } finally {
    // A superseded fetch must not clear the loading state of the fetch that replaced it.
    if (isCurrent()) sink.setLoading(false)
  }
}
